import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Pencil, Trash2, X, Check, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TG_BADGE = {
  sent: 'bg-sky-50 text-[#23ACD5]',
  failed: 'bg-red-50 text-[#E31A1A]',
  pending: 'bg-[#F4F7FE] text-[#A3AED0]',
};

export const ManageSalesModal = ({ open, onOpenChange, onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [sales, setSales] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [testingTg, setTestingTg] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const testTelegram = async () => {
    setTestingTg(true);
    try {
      await axios.post(`${API}/telegram/test`, {}, getAuthHeader());
      toast.success('Test message posted to the Breeze Financial Telegram group');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Telegram test failed');
    } finally {
      setTestingTg(false);
    }
  };

  const retryTelegram = async (id) => {
    setRetryingId(id);
    try {
      const res = await axios.post(`${API}/sales/${id}/telegram/retry`, {}, getAuthHeader());
      if (res.data.success) toast.success('Telegram notification sent');
      else toast.error(res.data.error || 'Telegram notification failed again');
      await fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/admin/list?period=${period}&limit=200`, getAuthHeader());
      setSales(res.data.sales || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [period, getAuthHeader]);

  useEffect(() => {
    if (!open) return;
    fetchSales();
    if (!config) {
      axios.get(`${API}/sales/config`, getAuthHeader())
        .then((res) => setConfig(res.data))
        .catch(() => {});
    }
  }, [open, fetchSales]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      client_name: s.client_name,
      carrier: s.carrier,
      product_type: s.product_type,
      status: s.status,
      annual_premium: String(s.annual_premium),
      sale_date: s.sale_date,
      lead_source: s.lead_source || '',
      target_premium: s.target_premium != null ? String(s.target_premium) : '',
    });
  };

  const save = async (id) => {
    setBusy(true);
    try {
      const toAmount = (v) => parseFloat(String(v).replace(/[^0-9.]/g, ''));
      const amount = toAmount(draft.annual_premium);
      const isIUL = draft.product_type === 'IUL';
      const targetPremium = isIUL && draft.target_premium ? toAmount(draft.target_premium) : null;
      await axios.patch(
        `${API}/sales/${id}`,
        { ...draft, annual_premium: amount, lead_source: draft.lead_source || null, target_premium: targetPremium },
        getAuthHeader()
      );
      toast.success('Sale updated');
      setEditingId(null);
      await fetchSales();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update sale');
    } finally {
      setBusy(false);
    }
  };

  const voidSale = async (s) => {
    if (!window.confirm(`Void ${s.agent_name}'s ${s.product_type} sale for ${s.client_name}? This removes it from the leaderboard.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/sales/${s.id}`, getAuthHeader());
      toast.success('Sale voided');
      await fetchSales();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to void sale');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[820px]" data-testid="manage-sales-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] text-xl font-extrabold text-[#2B3674]">
            Manage Submitted Sales
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full bg-[#F4F7FE] p-1" data-testid="manage-sales-period-toggle">
            {[
              { key: 'weekly', label: 'This sales week' },
              { key: 'all', label: 'All time' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                  period === p.key ? 'bg-white text-[#2447D6] shadow-sm' : 'text-[#A3AED0] hover:text-[#2B3674]'
                }`}
                data-testid={`manage-sales-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={testTelegram}
            disabled={testingTg}
            className="flex items-center gap-2 rounded-full border border-[#E9EDF7] px-4 py-1.5 text-xs font-bold text-[#2B3674] transition-colors hover:bg-[#F4F7FE] disabled:opacity-60"
            data-testid="telegram-test-btn"
          >
            {testingTg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 text-[#23ACD5]" />}
            Test Telegram
          </button>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" />
          </div>
        ) : sales.length === 0 ? (
          <p className="py-10 text-center text-sm font-semibold text-[#A3AED0]">No sales submitted in this period.</p>
        ) : (
          <div className="space-y-3" data-testid="manage-sales-list">
            {sales.map((s) => {
              const editing = editingId === s.id;
              return (
                <div
                  key={s.id}
                  className="rounded-2xl border border-[#E9EDF7] p-4"
                  data-testid={`manage-sales-row-${s.id}`}
                >
                  {editing ? (
                    <div className="space-y-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-[#A3AED0]">{s.agent_name}</p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label>Client</Label>
                          <Input
                            value={draft.client_name}
                            onChange={(e) => setDraft((d) => ({ ...d, client_name: e.target.value }))}
                            data-testid="manage-sales-client-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Carrier</Label>
                          <Select value={draft.carrier} onValueChange={(v) => setDraft((d) => ({ ...d, carrier: v }))}>
                            <SelectTrigger data-testid="manage-sales-carrier-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-64">
                              {(config?.carriers || [s.carrier]).map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>Product</Label>
                          <Select value={draft.product_type} onValueChange={(v) => setDraft((d) => ({ ...d, product_type: v }))}>
                            <SelectTrigger data-testid="manage-sales-product-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(config?.product_types || [s.product_type]).map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>Status</Label>
                          <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                            <SelectTrigger data-testid="manage-sales-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="underwriting">In underwriting</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>{draft.product_type === 'Annuity' ? 'Rollover amount' : 'Annual premium'}</Label>
                          <Input
                            inputMode="decimal"
                            value={draft.annual_premium}
                            onChange={(e) => setDraft((d) => ({ ...d, annual_premium: e.target.value }))}
                            data-testid="manage-sales-amount-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Sale date</Label>
                          <Input
                            type="date"
                            value={draft.sale_date}
                            onChange={(e) => setDraft((d) => ({ ...d, sale_date: e.target.value }))}
                            data-testid="manage-sales-date-input"
                          />
                        </div>
                        {draft.product_type === 'IUL' ? (
                          <div className="space-y-1">
                            <Label>Target premium</Label>
                            <Input
                              inputMode="decimal"
                              value={draft.target_premium}
                              onChange={(e) => setDraft((d) => ({ ...d, target_premium: e.target.value }))}
                              data-testid="manage-sales-target-premium-input"
                            />
                          </div>
                        ) : null}
                        <div className="space-y-1">
                          <Label>Lead source</Label>
                          <Select value={draft.lead_source} onValueChange={(v) => setDraft((d) => ({ ...d, lead_source: v }))}>
                            <SelectTrigger data-testid="manage-sales-lead-source-select"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {(config?.lead_sources || []).map((l) => (
                                <SelectItem key={l} value={l}>{l}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => save(s.id)}
                          disabled={busy}
                          className="flex items-center gap-2 rounded-full bg-[#01B574] px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
                          data-testid="manage-sales-save-btn"
                        >
                          <Check className="h-4 w-4" /> Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="flex items-center gap-2 rounded-full border border-[#E9EDF7] px-5 py-2 text-sm font-bold text-[#2B3674]"
                          data-testid="manage-sales-cancel-btn"
                        >
                          <X className="h-4 w-4" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-bold text-[#2B3674]">{s.agent_name}</p>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              s.status === 'approved' ? 'bg-emerald-50 text-[#01B574]' : 'bg-orange-50 text-[#FF9000]'
                            }`}
                          >
                            {s.status === 'approved' ? 'APPROVED' : 'UNDERWRITING'}
                          </span>
                          <span className="rounded-full bg-[#F4F7FE] px-2 py-0.5 text-[10px] font-bold text-[#2447D6]">
                            {s.category === 'annuity' ? 'ANNUITY' : 'LIFE'}
                          </span>
                          {s.telegram_notification_status ? (
                            <span
                              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${TG_BADGE[s.telegram_notification_status] || TG_BADGE.pending}`}
                              title={s.telegram_error || ''}
                              data-testid={`telegram-status-${s.id}`}
                            >
                              <Send className="h-2.5 w-2.5" />
                              TG {s.telegram_notification_status.toUpperCase()}
                            </span>
                          ) : null}
                          {s.telegram_notification_status === 'failed' ? (
                            <button
                              type="button"
                              onClick={() => retryTelegram(s.id)}
                              disabled={retryingId === s.id}
                              className="flex items-center gap-1 rounded-full border border-[#E9EDF7] px-2 py-0.5 text-[10px] font-bold text-[#2B3674] hover:bg-[#F4F7FE] disabled:opacity-60"
                              data-testid={`telegram-retry-${s.id}`}
                            >
                              {retryingId === s.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RefreshCw className="h-2.5 w-2.5" />}
                              Retry
                            </button>
                          ) : null}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-[#A3AED0]">
                          {s.client_name} · {s.carrier} · {s.product_type} · {s.sale_date}
                          {s.lead_source ? ` · ${s.lead_source}` : ''}
                          {s.target_premium ? ` · Target $${Math.round(s.target_premium).toLocaleString()}` : ''}
                        </p>
                      </div>
                      <p className="font-['Manrope'] text-base font-extrabold text-[#2B3674]">
                        ${Math.round(s.annual_premium).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(s)}
                          className="rounded-full border border-[#E9EDF7] p-2 text-[#2447D6] transition-colors hover:bg-[#F4F7FE]"
                          title="Edit"
                          data-testid={`manage-sales-edit-${s.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => voidSale(s)}
                          disabled={busy}
                          className="rounded-full border border-rose-100 p-2 text-rose-500 transition-colors hover:bg-rose-50 disabled:opacity-60"
                          title="Void"
                          data-testid={`manage-sales-void-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
